/**
 * The colour theme: your choice (`system`, `light` or `dark`, and whether dark is true black), the theme it means
 * here, and showing it. The page is drawn in it from the start: theme-boot.ts, a tiny script before the page draws,
 * reads the choice this browser kept and the device's light/dark setting; once you're known, your profile's choice
 * wins (and is kept for next time). The themes themselves are CSS (themes.gen.css): `data-theme` on <html> picks one.
 */
import { signal } from "@preact/signals";
import type { Theme } from "../api/types.gen.ts";
import { post, shell } from "./native.ts";
import { THEMES } from "./themes.gen.ts";

export type ThemeName = keyof typeof THEMES;

/** What you chose: light, dark or the device's, and whether dark (chosen or the device's) is true black. */
export interface ThemeChoice {
  theme: Theme;
  trueBlack: boolean;
}

/** Where this browser keeps your choice, for the next visit's first paint (none: the device's; none: warm dark). */
export const THEME_KEY = "revisa.theme";
export const TRUE_BLACK_KEY = "revisa.trueBlack";
/** The device's dark setting. */
const DARK = "(prefers-color-scheme: dark)";

const isTheme = (v: string): v is ThemeName => Object.hasOwn(THEMES, v);

/** A kept choice, as one: `system` for none or one this version doesn't know. */
export const asChoice = (raw: string | null | undefined): Theme => (raw === "light" || raw === "dark" ? raw : "system");

/** The theme a choice means on a device that's dark or not. */
export function resolveTheme(choice: string | null | undefined, trueBlack: boolean, dark: boolean): ThemeName {
  const c = asChoice(choice);
  const isDark = c === "system" ? dark : c === "dark";
  if (!isDark) {
    return "paper";
  }
  return trueBlack ? "night" : "dusk";
}

/** The choice this browser kept (none, or no storage: the device's, warm dark). */
export function keptChoice(): ThemeChoice {
  try {
    return {
      theme: asChoice(localStorage.getItem(THEME_KEY)),
      trueBlack: localStorage.getItem(TRUE_BLACK_KEY) === "1",
    };
  } catch {
    return { theme: "system", trueBlack: false };
  }
}

const prefersDark = (): boolean => globalThis.matchMedia?.(DARK).matches ?? false;

/** A `<meta name>` in the head, made if missing. */
function meta(doc: Document, name: string): HTMLMetaElement {
  let m = doc.head.querySelector<HTMLMetaElement>(`meta[name="${name}"]`);
  if (!m) {
    m = doc.createElement("meta");
    m.name = name;
    doc.head.append(m);
  }
  return m;
}

/** Whether the app is shown in a dark theme now (what's drawn in it, such as a document's colours, can follow). */
export const appDark = signal(false);
/** The theme shown now (what's read off the page in its colours, read again when it changes). */
export const themeShown = signal<ThemeName>("paper");

/** Shows `name`: on <html>, and in the browser's chrome (its colour, and its controls' light or dark). */
export function paint(name: ThemeName, doc: Document = document): void {
  doc.documentElement.dataset["theme"] = name;
  appDark.value = THEMES[name].scheme === "dark";
  themeShown.value = name;
  meta(doc, "theme-color").content = THEMES[name].chrome;
  meta(doc, "color-scheme").content = THEMES[name].scheme;
}

/** The theme shown now (paper before any is). */
export const shownTheme = (doc: Document = document): ThemeName => {
  const t = doc.documentElement.dataset["theme"] ?? "";
  return isTheme(t) ? t : "paper";
};

/** The theme's colours a native app draws with (its sidebar, its selection, its text, its accent): the CSS variable of
 *  each, as the page's own sidebar uses them. */
const NATIVE_COLORS = {
  sidebar: "--color-zinc-100",
  selected: "--color-zinc-200",
  text: "--color-zinc-900",
  muted: "--color-zinc-500",
  accent: "--color-primary",
} as const;

/** The shown theme's colours for a native app, as `#rrggbb` (the theme's are oklch: a canvas turns them into sRGB). */
function nativeColors(doc: Document): Record<keyof typeof NATIVE_COLORS, string> | null {
  const ctx = doc.createElement("canvas").getContext("2d", { willReadFrequently: true });
  if (!ctx) {
    return null;
  }
  const css = getComputedStyle(doc.documentElement);
  const hex = (name: string): string => {
    ctx.clearRect(0, 0, 1, 1);
    ctx.fillStyle = "#000";
    ctx.fillStyle = css.getPropertyValue(name).trim();
    ctx.fillRect(0, 0, 1, 1);
    const [r = 0, g = 0, b = 0] = ctx.getImageData(0, 0, 1, 1).data;
    return `#${[r, g, b].map((x) => x.toString(16).padStart(2, "0")).join("")}`;
  };
  return Object.fromEntries(Object.entries(NATIVE_COLORS).map(([k, v]) => [k, hex(v)])) as Record<
    keyof typeof NATIVE_COLORS,
    string
  >;
}

let current: ThemeChoice = { theme: "system", trueBlack: false };
let following: MediaQueryList | null = null;
const follow = () => {
  const name = resolveTheme(current.theme, current.trueBlack, prefersDark());
  paint(name);
  // A native app around the page follows the choice too (its bars, the keyboard), its edges in the page's colour, its
  // sidebar and accents in the theme's.
  if (shell()) {
    post("theme", {
      theme: current.theme,
      trueBlack: current.trueBlack,
      chrome: THEMES[name].chrome,
      colors: nativeColors(document),
    }).catch(() => undefined);
  }
};

/** Keeps `key` as `value`, or none. */
function keep(key: string, value: string | null): void {
  try {
    if (value === null) {
      localStorage.removeItem(key);
    } else {
      localStorage.setItem(key, value);
    }
  } catch {
    // Storage unavailable: the profile's choice still applies once you're known.
  }
}

/**
 * Shows the theme `next` means, keeps it for the next visit's first paint, and while it's `system` follows the
 * device's setting as it changes.
 */
export function applyTheme(next: ThemeChoice): void {
  followOthers();
  current = next;
  keep(THEME_KEY, next.theme === "system" ? null : next.theme);
  keep(TRUE_BLACK_KEY, next.trueBlack ? "1" : null);
  following?.removeEventListener("change", follow);
  following = next.theme === "system" ? (globalThis.matchMedia?.(DARK) ?? null) : null;
  following?.addEventListener("change", follow);
  follow();
}

let followingOthers = false;

/** Another tab (or page of a native app) changed the choice: this one shows it too. */
function followOthers(): void {
  if (followingOthers) {
    return;
  }
  followingOthers = true;
  globalThis.addEventListener?.("storage", (e) => {
    const { key } = e as StorageEvent;
    if (key !== THEME_KEY && key !== TRUE_BLACK_KEY) {
      return;
    }
    const kept = keptChoice();
    if (kept.theme !== current.theme || kept.trueBlack !== current.trueBlack) {
      applyTheme(kept);
    }
  });
}

/** Before the page draws (theme-boot.ts): the kept choice, on this device. */
export const bootTheme = (doc: Document = document): void => {
  const kept = keptChoice();
  paint(resolveTheme(kept.theme, kept.trueBlack, prefersDark()), doc);
};
